import { cn } from '@divlab/divanui';

import * as CatalogFiltrator from '@Stores/CatalogFiltrator';
import Page404 from '@Pages/Page404';
import useInfiniteFavorites from '@Queries/useInfiniteFavorites';
import LayoutMain from '@Layouts/LayoutMain';
import Button from '@UI/Button';
import ServicePageTitle from '@Components/ServicePageTitle';
import PageTopBar from '@Components/PageTopBar';
import usePage from '@Queries/usePage';
import useTranslation from '@Queries/useTranslation';
import SkeletPage from '@Skelets/SkeletPage';
import FavoritesCatalog from './elements/FavoritesCatalog';
import AuthorizationBanner from './elements/AuthorizationBanner';
import Page from '@Components/Page';
import { useProfile } from '@Queries/Profile';
import styles from './PageFavorites.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { PageFavoritesData } from './typings';
import type { BasePage } from '@Types/Base';
import type { FavoritesData } from '@Types/Favorites';
import type { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query';

export interface PageFavoritesProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  page: PageFavoritesData & BasePage;
  favorites: UseInfiniteQueryResult<InfiniteData<FavoritesData>>;
  slug: string;
}

const PageFavorites: FC<PageFavoritesProps> = (props) => {
  const { page, favorites, slug } = props;
  const profile = useProfile();
  const { t } = useTranslation();
  const [firstPage] = favorites.data.pages;
  const hasAppliedFilters = !!CatalogFiltrator.useAppliedValues().length;
  const hasAuthBanner = profile.isFetched && !profile.data;

  return (
    <>
      <PageTopBar breadcrumbs={page.breadcrumbs} />
      {(firstPage.productsTotalCount > 0 || hasAppliedFilters) && (
        <>
          <ServicePageTitle title={page.title} className={styles.wrapper} cnTitle={styles.title} />
          {hasAuthBanner && (
            <div className={cn(styles.wrapper, styles.banner, styles.bannerMobile)}>
              <AuthorizationBanner />
            </div>
          )}
          <FavoritesCatalog favorites={favorites} slug={slug} />
        </>
      )}
      {firstPage.productsTotalCount < 1 && !hasAppliedFilters && (
        <>
          <div className={cn(styles.wrapper)}>
            {hasAuthBanner && (
              <div className={cn(styles.banner, styles.bannerMobile)}>
                <AuthorizationBanner />
              </div>
            )}
            <div className={styles.emptyBlock}>
              <ServicePageTitle className={styles.title} title={t('empty-page.title')} />
              <Button className={styles.button} cnButton={styles.cnButton} to='/'>
                {t('empty-page.to-main-page')}
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const PageFavoritesContainer: FC = () => {
  const page = usePage<PageFavoritesData>();
  const favorites = useInfiniteFavorites();
  const [firstPage] = favorites.data?.pages || [];

  if (favorites.isPending) {
    return (
      <LayoutMain>
        <SkeletPage theme='gray' />
      </LayoutMain>
    );
  }

  if (firstPage.page !== 1 && firstPage.page > firstPage.pageCount) return <Page404 />;

  CatalogFiltrator.init({
    ...firstPage.filters,
    totalCount: firstPage.productsTotalCount,
  });

  return (
    <LayoutMain>
      <Page>
        <PageFavorites page={page.data} favorites={favorites} slug='favorites' />
      </Page>
    </LayoutMain>
  );
};

export default PageFavoritesContainer;
